<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div v-if="returnId" class="status-strip" :class="statusClass">
          <div>
            <span class="strip-label">Return No.</span>
            <strong>{{ header.billno || "-" }}</strong>
          </div>
          <span class="status-badge">{{ statusLabel }}</span>
          <p v-if="header.status === 'DRAFT'" class="strip-note">Draft &mdash; stock is not reduced until you confirm this return.</p>
          <p v-else-if="isReadonly" class="strip-note">This return is {{ statusLabel.toLowerCase() }} and can no longer be edited.</p>
        </div>

        <div v-if="loading" class="sr-card empty-panel">Loading purchase return...</div>

        <template v-else>
          <!-- Step 1: supplier -->
          <section class="sr-card">
            <div class="sr-step-head">
              <span class="sr-step">1</span>
              <div>
                <h4>Which supplier are you returning to?</h4>
                <p>Only items bought from this supplier and still in stock can be returned.</p>
              </div>
            </div>
            <div class="supplier-row">
              <div class="supplier-select">
                <model-list-select
                  v-model="header.vendor"
                  :list="vendorList"
                  option-value="vendorid"
                  option-text="vendorname"
                  placeholder="Search supplier..."
                  :isDisabled="Boolean(returnId)"
                />
              </div>
              <div v-if="selectedVendor" class="supplier-card">
                <strong>{{ selectedVendor.vendorname }}</strong>
                <small>
                  <template v-if="selectedVendor.gstin">GSTIN {{ selectedVendor.gstin }}</template>
                  <template v-if="selectedVendor.gstin && selectedVendor.phone"> &middot; </template>
                  <template v-if="selectedVendor.phone">{{ selectedVendor.phone }}</template>
                </small>
              </div>
            </div>
            <small v-if="returnId && !isReadonly" class="muted d-block mt-2">The supplier can't be changed on a saved return.</small>
          </section>

          <!-- Step 2: items -->
          <section class="sr-card">
            <div class="sr-step-head">
              <span class="sr-step">2</span>
              <div>
                <h4>Select the items being returned</h4>
                <p>Tick each batch you are sending back, then enter the quantity and reason.</p>
              </div>
            </div>

            <div v-if="!header.vendor" class="empty-panel">Choose a supplier first.</div>
            <div v-else-if="batchLoading" class="empty-panel">Loading items from this supplier...</div>
            <template v-else>
              <div v-if="!isReadonly && batchRows.length" class="filter-bar">
                <div class="lookup-bar">
                  <i class="fa fa-search" aria-hidden="true"></i>
                  <input v-model.trim="search" type="search" class="form-ctrl lookup-input" placeholder="Search item, batch or bill no..." />
                </div>
                <select v-model="billFilter" class="form-ctrl form-select bill-filter">
                  <option value="">All purchase bills</option>
                  <option v-for="bill in billOptions" :key="bill.value" :value="bill.value">{{ bill.label }}</option>
                </select>
                <span class="picked-count" :class="{ on: items.length }">{{ items.length }} selected</span>
              </div>

              <div v-if="!visibleRows.length" class="empty-panel">
                <template v-if="isReadonly">No items on this return.</template>
                <template v-else-if="batchRows.length">No items match your search.</template>
                <template v-else>Nothing from this supplier is in stock to return.</template>
              </div>
              <div v-else class="table-responsive">
                <table class="sr-table">
                  <thead>
                    <tr>
                      <th v-if="!isReadonly" class="col-check"></th>
                      <th>Item</th>
                      <th>Purchase bill</th>
                      <th class="num">In stock</th>
                      <th class="col-qty">Return Qty</th>
                      <th class="num">Rate</th>
                      <th class="col-reason">Reason</th>
                      <th class="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in visibleRows" :key="entry.key" :class="{ 'is-picked': entry.line }">
                      <td v-if="!isReadonly" class="col-check">
                        <input
                          type="checkbox"
                          :checked="!!entry.line"
                          :disabled="!entry.line && entry.available <= 0"
                          :aria-label="`Return ${entry.itemname}`"
                          @change="toggleBatch(entry.batch, $event.target.checked)"
                        />
                      </td>
                      <td>
                        <div class="item-name">{{ entry.itemname }}</div>
                        <div class="item-sub">
                          Batch {{ entry.batchno || "-" }}
                          <template v-if="entry.expirydate"> &middot; Exp {{ formatDate(entry.expirydate) }}</template>
                          <span v-if="isExpired(entry.expirydate)" class="warn-chip">Expired</span>
                        </div>
                      </td>
                      <td>
                        <div>{{ entry.billno || "-" }}</div>
                        <div v-if="entry.billdate" class="item-sub">{{ formatDate(entry.billdate) }}</div>
                      </td>
                      <td class="num">{{ entry.available === null ? "-" : formatQtyValue(entry.available) }}</td>
                      <td class="col-qty">
                        <div v-if="entry.line" class="qty-field">
                          <input
                            v-model.number="entry.line.returnqty"
                            type="number"
                            min="0"
                            :max="entry.line.maxreturnqty"
                            class="table-input qty-input"
                            :class="{ 'is-missing': showErrors && !(Number(entry.line.returnqty) > 0) }"
                            :disabled="isReadonly"
                            @input="normalizeLine(entry.line)"
                          />
                          <small v-if="!isReadonly">of {{ formatQtyValue(entry.line.maxreturnqty) }}</small>
                        </div>
                        <span v-else class="muted">Up to {{ formatQtyValue(entry.available) }}</span>
                      </td>
                      <td class="num">
                        Rs. {{ formatNumber(entry.rate) }}
                        <div v-if="entry.taxpercent" class="item-sub">{{ formatQtyValue(entry.taxpercent) }}% {{ entry.taxtype || "GST" }}{{ entry.istaxinclusive ? " incl." : "" }}</div>
                      </td>
                      <td class="col-reason">
                        <input
                          v-if="entry.line"
                          v-model="entry.line.reason"
                          list="purchase-return-reasons"
                          class="table-input"
                          placeholder="Why returned?"
                          :disabled="isReadonly"
                        />
                      </td>
                      <td class="num strong">{{ entry.line ? `Rs. ${formatNumber(entry.line.netamount)}` : "" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </section>

          <datalist id="purchase-return-reasons">
            <option v-for="reason in reasonSuggestions" :key="reason" :value="reason" />
          </datalist>

          <!-- Step 3: details + summary -->
          <div class="final-grid">
            <section class="sr-card">
              <div class="sr-step-head">
                <span class="sr-step">3</span>
                <div>
                  <h4>Return details</h4>
                  <p>Date and notes printed on the debit note.</p>
                </div>
              </div>
              <div class="details-grid">
                <div class="form-group">
                  <label class="form-label">Return Date</label>
                  <b-form-input v-model="header.returndate" type="date" class="form-ctrl" :disabled="isReadonly" />
                </div>
                <div class="form-group">
                  <label class="form-label">Overall reason <span class="muted">(optional)</span></label>
                  <b-form-input v-model="header.reason" list="purchase-return-reasons" class="form-ctrl" placeholder="e.g. Damaged in transit" :disabled="isReadonly" />
                </div>
                <div class="form-group details-full">
                  <label class="form-label">Remarks <span class="muted">(optional)</span></label>
                  <b-form-textarea v-model="header.remarks" rows="2" class="form-ctrl" :disabled="isReadonly" />
                </div>
              </div>
            </section>

            <section class="sr-card summary-card">
              <h4 class="summary-title">Debit Summary</h4>
              <div class="summary-row muted-row">
                <span>Items</span>
                <span>{{ items.length }} item(s), {{ formatQtyValue(summary.qty) }} qty</span>
              </div>
              <div class="summary-row">
                <span>Taxable value</span>
                <span>Rs. {{ formatNumber(summary.taxable) }}</span>
              </div>
              <div class="summary-row">
                <span>Tax</span>
                <span>Rs. {{ formatNumber(summary.totaltax) }}</span>
              </div>
              <div class="summary-net">
                <span>To recover from supplier</span>
                <strong>Rs. {{ formatNumber(summary.netamount) }}</strong>
              </div>
            </section>
          </div>
        </template>

        <div class="footer-actions">
          <router-link to="/purchasereturnlist" class="btn btn-cancel-light">Back to list</router-link>
          <div class="footer-right">
            <button v-if="returnId" class="btn btn-outline-secondary" :disabled="saving" @click="printNote()">
              <i class="fa fa-print me-1" aria-hidden="true"></i> {{ header.status === "DRAFT" ? "Preview" : "Print" }}
            </button>
            <button
              v-if="returnId && ['DRAFT', 'CONFIRMED'].includes(header.status)"
              class="btn btn-outline-danger"
              :disabled="saving"
              @click="cancelReturn()"
            >
              Cancel Return
            </button>
            <template v-if="!isReadonly">
              <button class="btn btn-outline-secondary" :disabled="saving" @click="saveDraft()">
                {{ saving ? "Saving..." : returnId ? "Save Changes" : "Save as Draft" }}
              </button>
              <button class="btn btn-submit" :disabled="saving" @click="saveAndConfirm()">
                {{ saving ? "Please wait..." : "Confirm Return" }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ModelListSelect } from "vue-search-select";
import axios from "axios";
import Swal from "sweetalert2";
import { openPdfPreview } from "@/assets/utils/pdfPreview";
import { fetchPurchaseReturnPdf } from "@/services/invoicePdf";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

export default {
  name: "createpurchasereturn",
  components: { ModelListSelect },
  data() {
    return {
      loading: false,
      saving: false,
      batchLoading: false,
      vendorList: [],
      availableBatches: [],
      items: [],
      search: "",
      billFilter: "",
      showErrors: false,
      reasonSuggestions: ["Damaged", "Expired", "Near expiry", "Defective", "Wrong item", "Excess quantity", "Quality issue"],
      header: {
        vendor: null,
        vendorname: "",
        billno: "",
        returndate: this.getToday(),
        locationid: this.$store.getters.getCurrentLocation?.locationid || "",
        status: "NEW",
        reason: "",
        remarks: "",
      },
    };
  },
  computed: {
    returnId() {
      return this.$route.query.id || "";
    },
    title() {
      return this.returnId ? "Edit Purchase Return" : "Create Purchase Return";
    },
    title1() {
      return this.returnId ? "Review or update this purchase return" : "Send items back to a supplier";
    },
    statusLabel() {
      const labels = { DRAFT: "Draft", CONFIRMED: "Confirmed", CANCELLED: "Cancelled" };
      return labels[this.header.status] || this.header.status || "";
    },
    selectedVendor() {
      return this.vendorList.find((vendor) => vendor.vendorid === this.header.vendor) || null;
    },
    batchRows() {
      const rows = this.availableBatches.map((batch) => this.toRow(batch, this.lineFor(batch.purchaseitemid)));
      // Lines saved on this return whose batch is no longer listed (e.g. fully returned) still need to show.
      const orphans = this.items
        .filter((line) => !this.availableBatches.some((batch) => batch.purchaseitemid === line.purchaseitemid))
        .map((line) => this.toRow({
          purchaseitemid: line.purchaseitemid,
          purchaseid: line.purchaseid,
          billno: line.purchasebillno,
          itemname: line.itemname,
          batchno: line.batchno,
          expirydate: line.expirydate,
          purchaseprice: line.purchaseprice,
          taxtype: line.taxtype,
          taxpercent: line.taxpercent,
          istaxinclusive: line.istaxinclusive,
          remainingqty: null,
        }, line));
      return [...rows, ...orphans];
    },
    visibleRows() {
      if (this.isReadonly) return this.batchRows.filter((entry) => entry.line);
      const term = this.search.toLowerCase();
      return this.batchRows.filter((entry) => {
        if (entry.line) return true;
        if (this.billFilter && entry.purchaseid !== this.billFilter) return false;
        if (!term) return true;
        return [entry.itemname, entry.batchno, entry.billno].some((text) => String(text || "").toLowerCase().includes(term));
      });
    },
    billOptions() {
      const seen = new Map();
      this.availableBatches.forEach((batch) => {
        if (!seen.has(batch.purchaseid)) {
          const date = batch.invoicedate ? ` (${this.formatDate(batch.invoicedate)})` : "";
          seen.set(batch.purchaseid, { value: batch.purchaseid, label: `${batch.billno || batch.purchaseid}${date}` });
        }
      });
      return Array.from(seen.values());
    },
    isReadonly() {
      return Boolean(this.returnId) && this.header.status !== "DRAFT";
    },
    statusClass() {
      return {
        "badge-draft": this.header.status === "DRAFT",
        "badge-confirmed": this.header.status === "CONFIRMED",
        "badge-cancelled": this.header.status === "CANCELLED",
      };
    },
    summary() {
      const totalamount = this.items.reduce((sum, line) => sum + Number(line.amount || 0), 0);
      const totaltax = this.items.reduce((sum, line) => sum + Number(line.taxamount || 0), 0);
      const netamount = this.items.reduce((sum, line) => sum + Number(this.lineTotal(line) || 0), 0);
      const taxable = this.items.reduce((sum, line) => sum + Number(line.taxablevalue || 0), 0);
      const qty = this.items.reduce((sum, line) => sum + Number(line.returnqty || 0), 0);
      return {
        totalamount: Number(totalamount.toFixed(2)),
        totaltax: Number(totaltax.toFixed(2)),
        netamount: Number(netamount.toFixed(2)),
        taxable: Number(taxable.toFixed(2)),
        qty,
      };
    },
    locationOptions() {
      return (this.$store.getters.getLocations || []).map((location) => ({
        value: location.locationid,
        text: location.locationname,
      }));
    },
  },
  watch: {
    "header.vendor": {
      async handler(newValue, oldValue) {
        if (!newValue || newValue === oldValue) return;
        if (!this.returnId) this.items = [];
        this.search = "";
        this.billFilter = "";
        await this.fetchReturnableBatches();
      },
    },
    "header.locationid": {
      async handler(newValue, oldValue) {
        if (!newValue || newValue === oldValue || !this.header.vendor) return;
        if (!this.returnId) this.items = [];
        await this.fetchReturnableBatches();
      },
    },
  },
  async beforeMount() {
    await this.fetchVendorList();
    if (this.returnId) {
      await this.loadReturn();
    }
  },
  methods: {
    getToday() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    },
    getCurrentUserId() {
      const attrs = this.$store?.getters?.getUserDetails;
      if (!Array.isArray(attrs)) return null;
      const sub = attrs.find((attr) => attr.Name === "sub");
      return sub?.Value || null;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatQty(quantity, freequantity) {
      const qty = Number(quantity || 0);
      const free = Number(freequantity || 0);
      return free ? `${qty} + ${free}` : `${qty}`;
    },
    formatQtyValue(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
    },
    isExpired(value) {
      if (!value) return false;
      return new Date(value) < new Date(`${this.getToday()}T00:00:00`);
    },
    lineFor(purchaseitemid) {
      return this.items.find((line) => line.purchaseitemid === purchaseitemid) || null;
    },
    toRow(batch, line) {
      return {
        key: batch.purchaseitemid,
        batch,
        line,
        purchaseid: batch.purchaseid,
        billno: batch.billno || batch.purchasebillno,
        billdate: batch.invoicedate,
        itemname: batch.itemname,
        batchno: batch.batchno,
        expirydate: batch.expirydate,
        available: batch.remainingqty === null || batch.remainingqty === undefined ? null : Number(batch.remainingqty),
        rate: Number(batch.purchaseprice || 0),
        taxpercent: Number(batch.taxpercent || 0),
        taxtype: batch.taxtype,
        istaxinclusive: Boolean(batch.istaxinclusive),
      };
    },
    toggleBatch(batch, checked) {
      if (this.isReadonly) return;
      if (checked) {
        this.addBatch(batch);
      } else {
        this.removeLine(batch.purchaseitemid);
      }
    },
    async printNote() {
      if (!this.returnId) return;
      Swal.fire({ title: "Preparing debit note...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      try {
        const { blob, fileName } = await fetchPurchaseReturnPdf(this.returnId);
        Swal.close();
        openPdfPreview(blob, { title: `Debit Note ${this.header.billno || ""}`.trim(), fileName });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Print Error", text: "Could not load the debit note PDF." });
      }
    },
    lineTotal(line) {
      const amount = Number(line.amount || 0);
      const tax = Number(line.taxamount || 0);
      return line.istaxinclusive ? amount : (amount + tax);
    },
    async fetchVendorList() {
      try {
        const response = await axios.get("/v1/vendors");
        this.vendorList = response.data || [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load suppliers." });
      }
    },
    async fetchReturnableBatches() {
      if (!this.header.vendor) {
        this.availableBatches = [];
        return;
      }
      this.batchLoading = true;
      try {
        const response = await axios.get("/v1/purchase-returns/returnable-batches", {
          params: {
            vendor_id: this.header.vendor,
            locationid: this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
            current_returnid: this.returnId || undefined,
          },
        });
        this.availableBatches = response.data || [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load returnable batches." });
      } finally {
        this.batchLoading = false;
      }
    },
    mapLineFromBatch(batch, overrides = {}) {
      const returnqty = Number(overrides.returnqty ?? 1);
      const purchaseprice = Number(batch.purchaseprice || 0);
      const taxpercent = Number(batch.taxpercent || 0);
      const istaxinclusive = Boolean(batch.istaxinclusive);
      const gross = Number((returnqty * purchaseprice).toFixed(2));
      const taxablevalue = istaxinclusive && taxpercent > 0
        ? Number((gross * 100 / (100 + taxpercent)).toFixed(2))
        : gross;
      const taxamount = istaxinclusive
        ? Number((gross - taxablevalue).toFixed(2))
        : Number((taxablevalue * taxpercent / 100).toFixed(2));
      const cgst = Number(overrides.cgst ?? (taxamount / 2)).toFixed(2);
      const sgst = Number(overrides.sgst ?? (taxamount / 2)).toFixed(2);
      const igst = Number(0).toFixed(2);
      const amount = istaxinclusive ? gross : taxablevalue;
      return {
        purchaseitemid: batch.purchaseitemid,
        purchaseid: batch.purchaseid,
        purchasebillno: batch.purchasebillno || batch.billno,
        itemid: batch.itemid,
        itemname: batch.itemname,
        batchno: batch.batchno,
        expirydate: batch.expirydate,
        purchaseprice,
        taxtype: batch.taxtype || "GST",
        taxpercent,
        taxablevalue,
        cgst: Number(cgst),
        sgst: Number(sgst),
        igst: Number(igst),
        istaxinclusive,
        maxreturnqty: Number(batch.remainingqty || 0),
        returnqty,
        amount,
        taxamount,
        netamount: Number(this.lineTotal({ amount, taxamount, istaxinclusive }).toFixed(2)),
        reason: overrides.reason || "",
      };
    },
    hasSelectedBatch(purchaseitemid) {
      return this.items.some((line) => line.purchaseitemid === purchaseitemid);
    },
    addBatch(batch) {
      if (this.isReadonly) return;
      if (this.hasSelectedBatch(batch.purchaseitemid)) return;
      this.items.push(this.mapLineFromBatch(batch));
    },
    removeLine(purchaseitemid) {
      this.items = this.items.filter((line) => line.purchaseitemid !== purchaseitemid);
    },
    normalizeLine(line) {
      const max = Number(line.maxreturnqty || 0);
      let qty = Number(line.returnqty || 0);
      if (qty < 0) qty = 0;
      if (qty > max) qty = max;
      line.returnqty = qty;
      const gross = Number((qty * Number(line.purchaseprice || 0)).toFixed(2));
      if (line.istaxinclusive && Number(line.taxpercent || 0) > 0) {
        line.taxablevalue = Number((gross * 100 / (100 + Number(line.taxpercent || 0))).toFixed(2));
        line.amount = gross;
        line.taxamount = Number((gross - line.taxablevalue).toFixed(2));
      } else {
        line.taxablevalue = gross;
        line.amount = gross;
        line.taxamount = Number((line.taxablevalue * Number(line.taxpercent || 0) / 100).toFixed(2));
      }
      line.cgst = Number((line.taxamount / 2).toFixed(2));
      line.sgst = Number((line.taxamount / 2).toFixed(2));
      line.igst = 0;
      line.netamount = Number(this.lineTotal(line).toFixed(2));
    },
    buildPayload() {
      return {
        vendor: this.header.vendor,
        locationid: this.header.locationid || null,
        returndate: this.header.returndate,
        reason: this.header.reason || null,
        remarks: this.header.remarks || null,
        createdby: this.getCurrentUserId(),
        items: this.items.map((line) => ({
          purchaseitemid: line.purchaseitemid,
          returnqty: Number(line.returnqty || 0),
          reason: line.reason || null,
        })),
      };
    },
    validateDraft() {
      const fail = (text) => {
        this.showErrors = true;
        Swal.fire({ icon: "warning", title: "Almost there", text });
        return false;
      };
      if (!this.header.vendor) return fail("Choose the supplier you are returning to.");
      if (!this.items.length) return fail("Tick at least one item to return.");
      for (const line of this.items) {
        const name = line.itemname || "this item";
        if (!(Number(line.returnqty) > 0)) return fail(`Enter a return quantity for ${name}.`);
        if (Number(line.returnqty) > Number(line.maxreturnqty)) {
          return fail(`Only ${this.formatQtyValue(line.maxreturnqty)} of ${name} is in stock to return.`);
        }
      }
      this.showErrors = false;
      return true;
    },
    async saveDraft({ silent = false } = {}) {
      if (!this.validateDraft()) return false;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.returnId) {
          await axios.put(`/v1/purchase-returns/${this.returnId}`, payload);
          if (!silent) Swal.fire({ icon: "success", title: "Changes saved", timer: 1400, showConfirmButton: false });
        } else {
          const response = await axios.post("/v1/purchase-returns", payload);
          const newId = response.data?.[0];
          if (!silent) Swal.fire({ icon: "success", title: "Draft saved", timer: 1400, showConfirmButton: false });
          if (newId) {
            await this.$router.replace({ path: "/createpurchasereturn", query: { id: newId } });
            await this.loadReturn();
          }
        }
        return true;
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to save purchase return draft." });
        return false;
      } finally {
        this.saving = false;
      }
    },
    async saveAndConfirm() {
      if (!this.validateDraft()) return;
      const supplier = this.selectedVendor?.vendorname || "the supplier";
      const result = await Swal.fire({
        title: "Confirm this return?",
        html: `Return ${this.items.length} item(s) to <strong>${supplier.replace(/[<>&]/g, "")}</strong> worth <strong>Rs. ${this.formatNumber(this.summary.netamount)}</strong>.<br><small>Stock will be reduced now. This cannot be edited afterwards.</small>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, confirm",
      });
      if (!result.isConfirmed) return;
      const saved = await this.saveDraft({ silent: true });
      if (!saved || !this.returnId) return;
      await this.confirmReturn({ skipPrompt: true });
    },
    async loadReturn() {
      if (!this.returnId) return;
      this.loading = true;
      try {
        const response = await axios.get(`/v1/purchase-returns/${this.returnId}`);
        const detail = response.data;
        this.header = {
          vendor: detail.vendor,
          vendorname: detail.vendorname || "",
          billno: detail.billno,
          returndate: detail.returndate || this.getToday(),
          locationid: detail.locationid || this.$store.getters.getCurrentLocation?.locationid || "",
          status: detail.status,
          reason: detail.reason || "",
          remarks: detail.remarks || "",
        };
        await this.fetchReturnableBatches();
        this.items = (detail.items || []).map((item) => {
          const batch = this.availableBatches.find((row) => row.purchaseitemid === item.purchaseitemid) || {
            purchaseitemid: item.purchaseitemid,
            purchaseid: item.purchaseid,
            billno: item.purchasebillno,
            itemid: item.itemid,
            itemname: item.itemname,
            batchno: item.batchno,
            expirydate: item.expirydate,
            purchaseprice: item.purchaseprice,
            taxtype: item.taxtype,
            taxpercent: item.taxpercent,
            taxablevalue: item.taxablevalue,
            cgst: item.cgst,
            sgst: item.sgst,
            igst: item.igst,
            istaxinclusive: item.istaxinclusive,
            remainingqty: item.returnqty,
          };
          return this.mapLineFromBatch(batch, {
            returnqty: item.returnqty,
            cgst: item.cgst,
            sgst: item.sgst,
            igst: item.igst,
            reason: item.reason,
          });
        });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load purchase return." });
      } finally {
        this.loading = false;
      }
    },
    async confirmReturn({ skipPrompt = false } = {}) {
      if (!skipPrompt) {
        const result = await Swal.fire({
          title: "Confirm purchase return?",
          text: "This will reduce stock and batch remaining quantity.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, confirm",
        });
        if (!result.isConfirmed) return;
      }

      this.saving = true;
      try {
        await axios.post(`/v1/purchase-returns/${this.returnId}/confirm`, {
          actorid: this.getCurrentUserId(),
        });
        Swal.fire({ icon: "success", title: "Return confirmed", timer: 1400, showConfirmButton: false });
        await this.loadReturn();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to confirm purchase return." });
      } finally {
        this.saving = false;
      }
    },
    async cancelReturn() {
      const result = await Swal.fire({
        title: "Cancel purchase return?",
        text: this.header.status === "CONFIRMED"
          ? "This will reverse the stock movement with a new ledger entry."
          : "This draft return will be cancelled.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it",
      });
      if (!result.isConfirmed) return;

      this.saving = true;
      try {
        await axios.post(`/v1/purchase-returns/${this.returnId}/cancel`, {
          actorid: this.getCurrentUserId(),
        });
        Swal.fire({ icon: "success", title: "Return cancelled", timer: 1400, showConfirmButton: false });
        await this.loadReturn();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to cancel purchase return." });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.sr-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 16px;
}
.sr-step-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}
.sr-step {
  flex: 0 0 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff4e8;
  color: #c2410c;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sr-step-head h4 {
  margin: 2px 0 2px;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}
.sr-step-head p {
  margin: 0;
  font-size: 0.82rem;
  color: #6b7280;
}

/* status strip */
.status-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 12px 18px;
  margin-bottom: 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
}
.strip-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}
.status-strip strong {
  font-size: 1rem;
  color: #111827;
}
.status-badge {
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #e5e7eb;
  color: #374151;
}
.strip-note {
  margin: 0 0 0 auto;
  font-size: 0.82rem;
  color: #6b7280;
}
.badge-draft { background: #fffaf3; border-color: #fed7aa; }
.badge-draft .status-badge { background: #ffedd5; color: #c2410c; }
.badge-confirmed { background: #f6fef9; border-color: #bbf7d0; }
.badge-confirmed .status-badge { background: #dcfce7; color: #15803d; }
.badge-cancelled { background: #fef8f8; border-color: #fecaca; }
.badge-cancelled .status-badge { background: #fee2e2; color: #dc2626; }

/* step 1 */
.type-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.type-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  padding: 14px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.type-option:hover:not(:disabled) { border-color: #fdba74; }
.type-option:disabled { cursor: not-allowed; opacity: 0.7; }
.type-option.active {
  border-color: #FF9F43;
  background: #fffaf5;
}
.type-radio {
  flex: 0 0 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  position: relative;
}
.type-option.active .type-radio { border-color: #FF9F43; }
.type-option.active .type-radio::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #FF9F43;
}
.type-copy { display: flex; flex-direction: column; gap: 4px; }
.type-copy strong { color: #111827; font-size: 0.92rem; }
.type-copy small { color: #6b7280; font-size: 0.8rem; line-height: 1.4; }
.type-tag {
  font-style: normal;
  font-size: 0.68rem;
  font-weight: 600;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  vertical-align: middle;
}

/* bill lookup */
.lookup-bar {
  position: relative;
  max-width: 480px;
  margin-bottom: 12px;
}
.lookup-bar i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}
.lookup-input {
  width: 100%;
  padding-left: 34px;
}
.sale-results {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  max-height: 340px;
  overflow-y: auto;
}
.sale-result {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #f1f2f4;
  background: #fff;
  text-align: left;
}
.sale-result:last-child { border-bottom: 0; }
.sale-result:hover:not(:disabled) { background: #fffaf5; }
.sale-result-main { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sale-result-main strong { color: #111827; font-size: 0.9rem; }
.sale-result-main small, .sale-result-side small { color: #6b7280; font-size: 0.78rem; }
.sale-result-side { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
.sale-result-side strong { color: #111827; font-size: 0.88rem; }
.sale-result-cta { color: #c2410c; font-weight: 600; font-size: 0.82rem; white-space: nowrap; }

.bill-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-bottom: 14px;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  background: #fffaf5;
}
.bill-card-meta { display: flex; gap: 28px; flex-wrap: wrap; }
.bill-card-meta span {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
}
.bill-card-meta strong { font-size: 0.9rem; color: #111827; }
.link-btn {
  border: 1px solid #fdba74;
  background: #fff;
  color: #c2410c;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 600;
}
.link-btn:hover { background: #fff4e8; }

/* tables */
.sr-table {
  width: 100%;
  border-collapse: collapse;
}
.sr-table th,
.sr-table td {
  padding: 10px 10px;
  border-bottom: 1px solid #f1f2f4;
  font-size: 0.85rem;
  vertical-align: middle;
}
.sr-table th {
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.sr-table tbody tr.is-picked td { background: #fffaf5; }
.sr-table tbody tr.is-unavailable td { color: #9ca3af; }
.sr-table .num { text-align: right; white-space: nowrap; }
.sr-table .strong { font-weight: 600; color: #111827; }
.col-check { width: 36px; }
.col-check input { width: 17px; height: 17px; accent-color: #FF9F43; cursor: pointer; }
.col-idx { width: 32px; }
.col-item { min-width: 190px; }
.col-batch { min-width: 170px; }
.col-qty { width: 120px; }
.col-rate { width: 110px; }
.col-tax { width: 80px; }
.col-cond { width: 150px; }
.col-reason { min-width: 170px; }
.item-name { font-weight: 500; color: #111827; }
.item-sub { font-size: 0.74rem; color: #6b7280; margin-top: 2px; }
.muted { color: #9ca3af; font-size: 0.8rem; }
.table-input {
  width: 100%;
  min-height: 34px;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  font-size: 0.85rem;
  background: #fff;
}
.table-input:focus { outline: none; border-color: #FF9F43; box-shadow: 0 0 0 2px rgba(255, 159, 67, 0.15); }
.table-input:disabled { background: #f9fafb; }
.table-input.is-missing { border-color: #f87171; background: #fef2f2; }
.qty-field { display: flex; align-items: center; gap: 6px; }
.qty-field small { color: #9ca3af; font-size: 0.75rem; white-space: nowrap; }
.qty-input { width: 72px; }

.cond-toggle {
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  overflow: hidden;
}
.cond-toggle button {
  border: 0;
  background: #fff;
  padding: 5px 10px;
  font-size: 0.76rem;
  font-weight: 600;
  color: #6b7280;
}
.cond-toggle button + button { border-left: 1px solid #e5e7eb; }
.cond-toggle button.on { background: #dcfce7; color: #15803d; }
.cond-toggle button.bad.on { background: #fee2e2; color: #dc2626; }
.cond-toggle button:disabled { cursor: default; }

.icon-btn {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #9ca3af;
  font-size: 1.2rem;
  line-height: 1;
}
.icon-btn:hover { background: #fef2f2; color: #dc2626; }
.add-line-btn {
  margin-top: 12px;
  border: 1px dashed #fdba74;
  background: #fffaf5;
  color: #c2410c;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.84rem;
  font-weight: 600;
}
.add-line-btn:hover { background: #fff4e8; }

.warn-chip {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 0.68rem;
  font-weight: 700;
}
.empty-panel {
  padding: 18px;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.85rem;
  text-align: center;
}
.warning-box {
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.84rem;
}

/* step 3 */
.final-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;
  align-items: start;
}
.details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.details-full { grid-column: 1 / -1; }
.form-label {
  display: block;
  margin-bottom: 6px;
  color: #374151;
  font-size: 0.82rem;
  font-weight: 600;
}
.form-ctrl {
  min-height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.summary-title {
  margin: 0 0 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}
.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  font-size: 0.86rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
}
.muted-row { color: #6b7280; font-size: 0.8rem; }
.input-row label { margin: 0; }
.summary-input { width: 120px; text-align: right; }
.summary-net {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff4e8;
  color: #c2410c;
}
.summary-net span { font-weight: 600; }
.summary-net strong { font-size: 1.3rem; }

/* footer */
.footer-actions {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-top: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 -4px 14px rgba(15, 23, 42, 0.06);
}
.footer-right { display: flex; gap: 10px; flex-wrap: wrap; }
.btn-cancel-light {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
}
.footer-actions .btn { min-height: 40px; padding: 8px 18px; font-weight: 600; }

@media (max-width: 1100px) {
  .final-grid { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .type-options,
  .details-grid { grid-template-columns: 1fr; }
  .sr-card { padding: 14px; }
  .strip-note { margin-left: 0; flex-basis: 100%; }
  .footer-actions, .footer-right { justify-content: stretch; }
  .footer-right .btn, .footer-actions > .btn { flex: 1; }
}

.supplier-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.supplier-select { flex: 0 1 420px; min-width: 240px; }
.supplier-select :deep(.ui.search.selection.dropdown) { min-height: 40px; border-radius: 8px; }
.supplier-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 14px;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  background: #fffaf5;
}
.supplier-card strong { color: #111827; font-size: 0.9rem; }
.supplier-card small { color: #6b7280; font-size: 0.78rem; }
.filter-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.filter-bar .lookup-bar { flex: 1 1 280px; margin-bottom: 0; }
.bill-filter { flex: 0 1 240px; min-height: 38px; }
.picked-count {
  padding: 4px 12px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}
.picked-count.on { background: #fff4e8; color: #c2410c; }
</style>
